import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatMoney } from '../ui/format';
import { Badge, EmptyState, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { StoredAvatar } from '../ui/StoredAvatar';
import {
  ENTITY_TYPE,
  getRecord,
  getTimeline,
  logActivity,
  updateRecord,
  type Activity,
  type BantAnswer,
  type BantCriterion,
  type CrmEntity,
  type LifecycleStage,
  type Qualification,
  type RecordDetail,
} from './detail-api';
import { EDITABLE, changesBetween, formOf, problemsIn, type RecordForm } from './record-form';
import { LifecyclePanel } from './LifecyclePanel';
import { QualificationPanel } from './QualificationPanel';
import { RecordSide } from './RecordSide';
import { InteractionBar } from './InteractionBar';
import { LeadSidebar } from './LeadSidebar';
import { TIMELINE_TABS, type TimelineTab } from './detail-api';

/**
 * One record.
 *
 * The timeline is the reason this screen exists. A CRM's value is not the fields — those are on the
 * list — it is being able to answer "what happened with this customer", and that is a chronology
 * somebody can add to (context.md section 16).
 *
 * The field panel is rendered from whatever the record has rather than from a per-entity layout:
 * custom fields are part of the product (section 106), so a screen with a hand-written field list
 * would be wrong the moment a tenant adds one.
 */

/** Fields that are plumbing rather than information, and never worth a row on the screen. */
const HIDDEN = new Set([
  'id',
  'tenant_id',
  'version',
  'deleted_at',
  'custom_fields',
  'utm',
  // Shown by the panels that understand them, not as two more rows of raw text.
  'lifecycle_stage',
  'lifecycle_changed_at',
  'bant',
  'qualification',
  'deal_value_cents',
  'sale_probability',
  'moment_id',
  'owner_id',
  'team_id',
  'created_by',
  'updated_by',
]);

const ACTIVITY_TONE: Record<string, 'neutral' | 'info' | 'positive' | 'warning'> = {
  CREATED: 'info',
  UPDATED: 'neutral',
  NOTE: 'neutral',
  CALL: 'info',
  EMAIL: 'info',
  MEETING: 'info',
  STATUS_CHANGED: 'warning',
  CONVERTED: 'positive',
  WON: 'positive',
};

export function RecordPage(): React.JSX.Element {
  const params = useParams<{ entity: string; id: string }>();

  const entity = params.entity as CrmEntity;
  const id = params.id!;
  const entityType = ENTITY_TYPE[entity];

  const client = useQueryClient();
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('NOTE');
  const [tab, setTab] = useState<TimelineTab>('all');
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RecordForm>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const record = useQuery({
    queryKey: ['record', entity, id],
    queryFn: () => getRecord(entity, id),
    enabled: Boolean(entityType),
  });

  const timeline = useQuery({
    queryKey: ['timeline', entityType, id],
    queryFn: () => getTimeline(entityType, id),
    enabled: Boolean(entityType),
  });

  /*
   * Which fields this kind of record lets somebody correct here.
   *
   * Declared per entity rather than derived from the data: a screen that offered every column would
   * offer `created_at` and `version`, and one that guessed from types would offer them the day
   * somebody adds a column. An opportunity has its own screen and is not in the list.
   */
  const editable = EDITABLE[entity] ?? [];
  const original = record.data ? formOf(editable, record.data) : {};
  const problems = problemsIn(editable, draft);
  const changes = changesBetween(editable, original, draft);
  const dirty = Object.keys(changes).length > 0;

  const save = useMutation({
    mutationFn: () => updateRecord(entity, id, changes),
    onSuccess: () => {
      setEditing(false);
      setSaveError(null);
      void client.invalidateQueries({ queryKey: ['record', entity, id] });
      // The change is on the timeline too: a corrected phone number is a thing that happened.
      void client.invalidateQueries({ queryKey: ['timeline', entityType, id] });
    },
    onError: (caught: unknown) => setSaveError(messageFor(caught)),
  });

  const add = useMutation({
    mutationFn: () => logActivity(entityType, id, { activityType: kind, body: note.trim() }),
    onSuccess: () => {
      setNote('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['timeline', entityType, id] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (!entityType) {
    return (
      <div className="view">
        <EmptyState title={t('error.notFound')} />
      </div>
    );
  }

  if (record.error) {
    return (
      <div className="view">
        <ErrorState error={record.error} onRetry={() => void record.refetch()} />
      </div>
    );
  }

  if (!record.data) {
    return (
      <div className="view">
        <Loading />
      </div>
    );
  }

  const wanted = TIMELINE_TABS[tab];
  const visible = (timeline.data?.activities ?? []).filter(
    (activity) => wanted === null || wanted.includes(activity.activity_type as never),
  );

  return (
    <div className="view view--wide">
      <PageHeader
        title={titleOf(record.data)}
        description={t(`crm.entity.${entityType}`)}
        /*
         * Their face, when this product has one (Phase 101).
         *
         * It arrives on a messaging channel and is kept in the bucket; the record is where somebody
         * looks to remember who they are dealing with. Initials behind it, so a contact who has
         * never written looks like a contact rather than a gap.
         */
        visual={
          entity === 'contacts' ? (
            <StoredAvatar
              name={titleOf(record.data)}
              fileId={(record.data as { avatar_file_id?: string | null }).avatar_file_id ?? null}
              size="lg"
            />
          ) : undefined
        }
        actions={
          <Link className="button button--quiet button--compact" to={listPath(entity)}>
            {t('record.backToList')}
          </Link>
        }
      />

      {/* Where this account is on the journey — the question the field list cannot answer. */}
      {(entity === 'companies' || entity === 'contacts') && (
        <Panel title={t('crm.lifecycle')}>
          <LifecyclePanel
            entity={entity}
            id={id}
            stage={(record.data.lifecycle_stage as LifecycleStage) ?? 'LEAD'}
            changedAt={(record.data.lifecycle_changed_at as string | null) ?? null}
          />
        </Panel>
      )}

      {entity === 'leads' && <InteractionBar leadId={id} />}

      {entity === 'leads' && isRecord(record.data.bant) && isRecord(record.data.qualification) && (
        <Panel title={t('crm.bant.title')}>
          <QualificationPanel
            id={id}
            bant={record.data.bant as Record<BantCriterion, BantAnswer>}
            qualification={record.data.qualification as unknown as Qualification}
          />
        </Panel>
      )}

      <div className="record record--three">
        <div className="record__main">
          <Panel
            title={t('record.fields')}
            actions={
              editable.length > 0 &&
              (editing ? (
                <span className="cell-inline">
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() => {
                      setDraft(formOf(editable, record.data));
                      setEditing(false);
                      setSaveError(null);
                    }}
                  >
                    {t('confirm.cancel')}
                  </button>
                  <button
                    type="button"
                    className="button button--primary button--compact"
                    disabled={save.isPending || Object.keys(problems).length > 0 || !dirty}
                    onClick={() => save.mutate()}
                  >
                    {save.isPending ? t('record.saving') : t('record.save')}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => {
                    setDraft(formOf(editable, record.data));
                    setEditing(true);
                  }}
                >
                  {t('record.edit')}
                </button>
              ))
            }
          >
            {/*
             * Editable where it makes sense, and one save for the lot.
             *
             * This screen showed a customer's e-mail address and offered no way to correct it —
             * fixing a typo meant going back to the list. Saving once from a button rather than on
             * every keystroke is the same reason the deal screen does: somebody correcting a name
             * and a phone number at once means one change, and a PATCH each is a race with itself.
             */}
            {editing ? (
              <div className="record-edit">
                {editable.map((field) => (
                  <label key={field.key} className="stack__field">
                    <span>
                      {t(`record.field.${field.key}`)}
                      {field.required && <span className="required"> *</span>}
                    </span>
                    <input
                      type={
                        field.kind === 'date' ? 'date' : field.kind === 'email' ? 'email' : 'text'
                      }
                      inputMode={field.kind === 'number' ? 'numeric' : undefined}
                      value={draft[field.key] ?? ''}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, [field.key]: event.target.value }))
                      }
                    />
                    {problems[field.key] && (
                      <span className="field-problem">
                        {t(`record.problem.${problems[field.key]!}`)}
                      </span>
                    )}
                  </label>
                ))}

                {saveError && (
                  <p className="alert" role="alert">
                    {saveError}
                  </p>
                )}
              </div>
            ) : (
              <dl className="details">
                {Object.entries(record.data)
                  .filter(([key, value]) => !HIDDEN.has(key) && value !== null && value !== '')
                  .map(([key, value]) => (
                    <div key={key} className="details__row">
                      <dt>{t(`record.field.${key}`)}</dt>
                      <dd>{renderValue(key, value)}</dd>
                    </div>
                  ))}
              </dl>
            )}

            {/* Custom fields are the tenant's own, so their keys are shown as the tenant wrote them. */}
            {isRecord(record.data.custom_fields) &&
              Object.keys(record.data.custom_fields).length > 0 && (
                <>
                  <h3 className="record__subhead">{t('record.customFields')}</h3>
                  <dl className="details">
                    {Object.entries(record.data.custom_fields).map(([key, value]) => (
                      <div key={key} className="details__row">
                        <dt>{key}</dt>
                        <dd>{String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
          </Panel>

          <Panel title={t('record.timeline')}>
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                if (note.trim()) add.mutate();
              }}
            >
              {/* What happened, not just "a note". A call and an e-mail read differently on a
                timeline somebody is trying to reconstruct a relationship from. */}
              <div className="composer">
                {MANUAL_KINDS.map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    className={`chip${kind === entry ? ' chip--active' : ''}`}
                    onClick={() => setKind(entry)}
                  >
                    {t(`activity.${entry}`)}
                  </button>
                ))}
              </div>

              <textarea
                className="record__note"
                rows={2}
                value={note}
                placeholder={t(`record.placeholder.${kind}`)}
                onChange={(event) => setNote(event.target.value)}
              />

              {error && (
                <p className="alert" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="button button--primary button--compact"
                disabled={note.trim() === '' || add.isPending}
              >
                {add.isPending ? t('work.creating') : t('record.addNote')}
              </button>
            </form>

            <nav className="tabs tabs--compact" aria-label={t('record.timeline')}>
              {(Object.keys(TIMELINE_TABS) as TimelineTab[]).map((entry) => (
                <button
                  key={entry}
                  type="button"
                  className={`tab${tab === entry ? ' is-active' : ''}`}
                  onClick={() => setTab(entry)}
                >
                  {t(`record.tab.${entry}`)}
                </button>
              ))}
            </nav>

            {timeline.isLoading ? (
              <Loading />
            ) : visible.length === 0 ? (
              <EmptyState title={t('record.noActivity')} />
            ) : (
              <ol className="timeline">
                {groupByMonth(visible).map(([month, entries]) => (
                  <li key={month} className="timeline__group">
                    <h3 className="timeline__month">{month}</h3>
                    <ol className="timeline">
                      {entries.map((activity) => (
                        <TimelineEntry key={activity.id} activity={activity} />
                      ))}
                    </ol>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </div>

        <aside className="record__side" aria-label={t('record.related')}>
          {entity === 'leads' && <LeadSidebar leadId={id} lead={record.data} />}

          <RecordSide entity={entity} entityType={entityType} id={id} record={record.data} />
        </aside>
      </div>
    </div>
  );
}

/** What somebody can put on a timeline by hand. The rest is written by the system. */
const MANUAL_KINDS = ['NOTE', 'CALL', 'MEETING', 'EMAIL', 'WHATSAPP', 'TASK'];

/**
 * The timeline, cut into months.
 *
 * A relationship is read backwards in chunks — "what happened in June" — and an undivided list of
 * ninety entries is a list nobody scrolls to the end of. The order inside each month is the order
 * the API returned, newest first.
 */
function groupByMonth(activities: Activity[]): [string, Activity[]][] {
  const groups = new Map<string, Activity[]>();

  for (const activity of activities) {
    const label = monthLabel(activity.occurred_at);

    groups.set(label, [...(groups.get(label) ?? []), activity]);
  }

  return [...groups.entries()];
}

function monthLabel(iso: string): string {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '—';

  const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date);

  return label.charAt(0).toUpperCase() + label.slice(1);
}

function TimelineEntry({ activity }: { activity: Activity }): React.JSX.Element {
  return (
    <li className="timeline__item">
      <span className="timeline__marker" aria-hidden="true" />

      <div className="timeline__body">
        <div className="cell-inline">
          <Badge tone={ACTIVITY_TONE[activity.activity_type] ?? 'neutral'}>
            {t(`activity.${activity.activity_type}`)}
          </Badge>
          <span className="muted small">{formatDateTime(activity.occurred_at)}</span>
          {activity.actor_type !== 'USER' && (
            <span className="muted small">{t(`audit.actor.${activity.actor_type}`)}</span>
          )}
        </div>

        {activity.subject && <p className="strong">{activity.subject}</p>}
        {activity.body && <p className="timeline__text">{activity.body}</p>}
      </div>
    </li>
  );
}

function titleOf(record: RecordDetail): string {
  for (const key of ['name', 'title', 'legal_name']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }

  const first = record.first_name;
  const last = record.last_name;

  if (typeof first === 'string')
    return [first, typeof last === 'string' ? last : ''].join(' ').trim();

  return record.id.slice(0, 8);
}

function listPath(entity: CrmEntity): string {
  return {
    leads: '/crm/leads',
    contacts: '/crm/contatos',
    companies: '/crm/empresas',
    opportunities: '/crm/oportunidades',
  }[entity];
}

/** Values are shown by what the key means, not by their JavaScript type. */
function renderValue(key: string, value: unknown): React.ReactNode {
  if (key.endsWith('_cents') && typeof value === 'number') return formatMoney(value);
  if (key.endsWith('_at') && typeof value === 'string') return formatDateTime(value);
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (isRecord(value)) return <code>{JSON.stringify(value)}</code>;

  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
